import { useEffect, useState } from "react";
import { dataApi } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Gauge, Zap, ZapOff, CheckCircle2, Sparkles } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function Workload() {
  const [data, setData] = useState(null);

  useEffect(() => {
    dataApi.workload().then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6">
      <PageHeader testid="workload-header" title="Workload Analysis" subtitle="Balance capacity across the org" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Overloaded" value={k.overloaded} icon={Zap} tone="danger" />
        <KpiCard label="Balanced" value={k.balanced} icon={CheckCircle2} tone="success" />
        <KpiCard label="Underutilized" value={k.underutilized} icon={ZapOff} tone="warning" />
        <KpiCard label="Total Employees" value={k.total} icon={Gauge} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard title="Department Utilization">
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={data.department_utilization} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={90} />
                <Tooltip />
                <Bar dataKey="utilization" fill="hsl(211 100% 55%)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="AI Recommendations" subtitle="Based on trailing 14-day patterns">
          <div className="space-y-3">
            {data.ai_suggestions.map((s, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="text-sm">{s}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Workload Distribution" subtitle="Top 20 by utilization">
        <div className="space-y-2">
          {data.workload_distribution.map((e) => (
            <div key={e.id} className="flex items-center gap-3">
              <div className="w-40 text-sm truncate">{e.name}</div>
              <Progress value={Math.min(e.utilization, 120) * (100 / 120)} className="h-2 flex-1" />
              <div className={`w-16 text-right number text-sm ${e.utilization > 90 ? "text-red-500" : e.utilization < 60 ? "text-amber-500" : ""}`}>{e.utilization}%</div>
              {e.utilization > 90 && <Badge variant="destructive" className="text-[10px]">Overloaded</Badge>}
              {e.utilization < 60 && <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500">Low</Badge>}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
